/**
 * @deprecated Migration-source only (v3→v4). Split into four locked sections —
 * `product-gallery`, `product-buybox`, `product-policies`, `product-related` —
 * which are what the default product template now uses. This schema stays
 * registered so un-migrated (pre-v4) stored themes still validate; it no longer
 * appears in the default product template. See `migrations/v3-to-v4.ts`.
 *
 * Product details — locked. The PDP's primary content (gallery, name, price,
 * variant picker, add-to-cart, description). Cannot be removed because it IS
 * the product page.
 *
 * Layout is chosen with a `variant` thumbnail tile picker (mirrors the hero's
 * `variant` pattern), gating a handful of per-variant "cohort" controls behind
 * `showIf: { variant: … }` so the sheet only shows knobs relevant to the pick:
 *   - `columns`  — the CURRENT PDP: a vertical thumb-rail slider gallery beside
 *                  a sticky info column. Exposes `thumbPosition` (rail on the
 *                  side vs below the main image).
 *   - `carousel` — a swipe/arrow track carousel, no thumb rail. Exposes
 *                  `carouselControls` (dots / arrows / both).
 *   - `stacked`  — every image stacked down the left column.
 *   - `mosaic`   — a multi-cell image grid in the left column. Exposes
 *                  `mosaicColumns` (2 vs 3).
 *
 * A few settings apply to every variant (`showRating`, `descriptionPlacement`,
 * `infoColumnWidth`), and the original peripheral toggles (related products,
 * trust badges, sticky CTA, policies) are kept unchanged.
 *
 * Lives under `templates.product` only. The home template never includes it.
 *
 * NOTE ON GLYPHS: the shared `heroVariantThumbs` registry is keyed by raw option
 * value. `carousel` and `mosaic` already exist there (hero variants), so those
 * two tiles reuse the hero sketches; only `columns` and `stacked` needed new
 * glyphs. Re-declaring `carousel`/`mosaic` would clobber the hero tiles.
 */
export const productDetailsSchema = {
    type: "product-details",
    name: { ar: "تفاصيل المنتج", en: "Product details" },
    description: {
        ar: "القسم الأساسي في صفحة المنتج (الصور، الاسم، السعر، الإضافة للسلة).",
        en: "The PDP core block: gallery, name, price, variants, add-to-cart.",
    },
    locked: true,
    // Deprecated: migration-source only (v3→v4). Kept registered so pre-v4 stored
    // data still validates as a known section type, but excluded from the required
    // locked sections (its four replacements are the required ones now).
    deprecated: true,
    criticalForSeo: true,
    maxPerTemplate: 1,
    availableOnTemplates: ["product"],
    settings: [
        {
            id: "variant",
            type: "select",
            ui: "thumbnail",
            label: { ar: "شكل صفحة المنتج", en: "Product page layout" },
            helpText: {
                ar: "اختر شكل عرض صور المنتج ومعلوماته. كل نوع له إعداداته الخاصة.",
                en: "Pick how the product gallery and info are laid out. Each type has its own settings.",
            },
            options: [
                // The current PDP: vertical thumb-rail slider + sticky info column.
                { value: "columns", label: { ar: "أعمدة (شريط صور)", en: "Columns" } },
                // Swipe/arrow track carousel, no thumb rail.
                { value: "carousel", label: { ar: "شرائح متحركة", en: "Carousel" } },
                // Every image stacked down the left column.
                { value: "stacked", label: { ar: "صور متتابعة", en: "Stacked" } },
                // EO avyro: a thumb rail beside a scrolling stack of every image.
                {
                    value: "stacked-rail",
                    label: { ar: "شريط + صور متتابعة", en: "Rail + stack" },
                },
                // Multi-cell image grid in the left column.
                { value: "mosaic", label: { ar: "فسيفساء", en: "Mosaic" } },
            ],
            default: "columns",
        },
        // --- Macro layout (shared across all gallery variants) ---------------
        {
            id: "productLayout",
            type: "select",
            label: { ar: "تخطيط الصفحة", en: "Page layout" },
            helpText: {
                ar: 'إما عمودين (صورة جنب المعلومات) أو صورة كبيرة بعرض الشاشة فوق معلومات في المنتصف (شكل معرض EasyOrders).',
                en: 'Either two columns (gallery beside the info) or a full-width hero image above centered info (EasyOrders gallery style).',
            },
            options: [
                { value: "two-column", label: { ar: "عمودين", en: "Two columns" } },
                {
                    value: "stacked-hero",
                    label: { ar: "صورة كبيرة ثم معلومات", en: "Full-width hero" },
                },
            ],
            default: "two-column",
        },
        // --- Columns cohort --------------------------------------------------
        {
            id: "thumbPosition",
            type: "select",
            label: { ar: "مكان شريط الصور", en: "Thumbnail rail position" },
            helpText: {
                ar: "مكان شريط الصور المصغّرة بالنسبة للصورة الكبيرة.",
                en: "Where the thumbnail rail sits relative to the main image.",
            },
            options: [
                { value: "left", label: { ar: "جنب الصورة", en: "Beside" } },
                { value: "below", label: { ar: "تحت الصورة", en: "Below" } },
            ],
            default: "left",
            showIf: { variant: ["columns"] },
        },
        // --- Carousel cohort -------------------------------------------------
        {
            id: "carouselControls",
            type: "select",
            label: { ar: "أدوات التنقل", en: "Carousel controls" },
            helpText: {
                ar: "شكل أدوات التنقل بين الصور: نقاط، أسهم، أو الاتنين.",
                en: "How the gallery navigation shows: dots, arrows, or both.",
            },
            options: [
                { value: "dots", label: { ar: "نقاط", en: "Dots" } },
                { value: "arrows", label: { ar: "أسهم", en: "Arrows" } },
                { value: "both", label: { ar: "نقاط وأسهم", en: "Both" } },
            ],
            default: "arrows",
            showIf: { variant: ["carousel"] },
        },
        // --- Mosaic cohort ---------------------------------------------------
        {
            id: "mosaicColumns",
            type: "select",
            label: { ar: "عدد أعمدة الفسيفساء", en: "Mosaic columns" },
            helpText: {
                ar: "عدد الأعمدة في شبكة الصور.",
                en: "Number of columns in the image grid.",
            },
            options: [
                { value: "2", label: { ar: "عمودين", en: "2 columns" } },
                { value: "3", label: { ar: "٣ أعمدة", en: "3 columns" } },
            ],
            default: "3",
            showIf: { variant: ["mosaic"] },
        },
        // --- Shared across all variants -------------------------------------
        {
            id: "infoColumnWidth",
            type: "select",
            label: { ar: "عرض عمود المعلومات", en: "Info column width" },
            helpText: {
                ar: "عرض عمود اسم المنتج والسعر وزر الشراء.",
                en: "Width of the name / price / add-to-cart column.",
            },
            options: [
                { value: "narrow", label: { ar: "ضيّق", en: "Narrow" } },
                { value: "medium", label: { ar: "متوسط", en: "Medium" } },
                { value: "wide", label: { ar: "واسع", en: "Wide" } },
            ],
            default: "medium",
        },
        {
            id: "descriptionPlacement",
            type: "select",
            label: { ar: "مكان وصف المنتج", en: "Description placement" },
            helpText: {
                ar: "الوصف في أكورديون تحت المنتج، ولا جوه تفاصيل المنتج.",
                en: "Show the description in an accordion below, or inside the product details.",
            },
            options: [
                {
                    value: "accordion-below",
                    label: { ar: "أكورديون تحت", en: "Accordion below" },
                },
                { value: "in-details", label: { ar: "جوه التفاصيل", en: "In details" } },
                {
                    value: "accordion-grouped",
                    label: {
                        ar: "أكورديون موحّد مع السياسات",
                        en: "Grouped with policies",
                    },
                },
            ],
            default: "accordion-below",
        },
        {
            id: "showRating",
            type: "checkbox",
            label: { ar: "إظهار تقييم النجوم", en: "Show star rating" },
            helpText: {
                ar: "تقييم نجوم صغير جنب اسم المنتج في عمود المعلومات.",
                en: "Inline star rating next to the name in the info column.",
            },
            default: false,
        },
        {
            id: "breadcrumbStyle",
            type: "select",
            label: { ar: "شكل مسار التنقّل", en: "Breadcrumb style" },
            helpText: {
                ar: 'إما بدون مسار، أو مسار مختصر (تسوق / المنتج) بحروف كبيرة، أو مسار كامل بالفئة (الرئيسية | الفئة | المنتج) بشكل EasyOrders.',
                en: 'None, a short uppercase path (Shop / product), or a full path including the category (Home | Category | product), EasyOrders style.',
            },
            options: [
                { value: "none", label: { ar: "بدون", en: "None" } },
                { value: "mono", label: { ar: "مختصر بحروف كبيرة", en: "Short (uppercase)" } },
                {
                    value: "path",
                    label: { ar: "كامل مع الفئة", en: "Full path with category" },
                },
            ],
            default: "mono",
        },
        // --- Buy area treatment (shared across all variants) -----------------
        {
            id: "ctaTreatment",
            type: "select",
            label: { ar: "شكل أزرار الشراء", en: "Buy button treatment" },
            helpText: {
                ar: 'إما زرين مملوءين، أو "أضف للسلة" بإطار و"اشترِ الآن" مملوء (شكل EasyOrders).',
                en: 'Both buttons filled, or an outlined "Add to cart" above a solid "Buy now" (EasyOrders style).',
            },
            options: [
                { value: "solid", label: { ar: "زرين مملوءين", en: "Both solid" } },
                {
                    value: "outline-add",
                    label: { ar: "إضافة بإطار + شراء مملوء", en: "Outline add + solid buy" },
                },
            ],
            default: "solid",
        },
        // --- Free-shipping progress bar -------------------------------------
        {
            id: "showFreeShippingBar",
            type: "checkbox",
            label: {
                ar: "شريط تقدّم الشحن المجاني",
                en: "Free-shipping progress bar",
            },
            helpText: {
                ar: 'شريط يوضّح كام باقي على الشحن المجاني (زي "باقي لك ٥٠٠٠ ج.م").',
                en: 'A bar showing how far the cart is from free shipping (e.g. "You\'re only 5000 EGP away").',
            },
            default: false,
        },
        {
            id: "freeShippingThreshold",
            type: "text",
            label: { ar: "حد الشحن المجاني", en: "Free-shipping threshold" },
            helpText: {
                ar: "قيمة السلة اللي بعدها الشحن يبقى مجاني (بعملة المتجر).",
                en: "Cart subtotal above which shipping is free (store currency).",
            },
            default: "",
            showIf: { showFreeShippingBar: true },
        },
        // --- In-buy-area cross-sell / upsell card ---------------------------
        {
            id: "showUpsellCard",
            type: "checkbox",
            label: {
                ar: "بطاقة منتجات مقترحة في منطقة الشراء",
                en: "Cross-sell card in the buy area",
            },
            helpText: {
                ar: "بطاقة صغيرة تحت أزرار الشراء تعرض منتجات مقترحة واحد ورا التاني (شكل EasyOrders).",
                en: "A compact card under the buy buttons stepping through suggested products (EasyOrders style).",
            },
            default: false,
        },
        // --- Original peripheral toggles (unchanged) ------------------------
        {
            id: "showRelatedProducts",
            type: "checkbox",
            label: { ar: 'إظهار قسم "منتجات مشابهة"', en: 'Show "Related products"' },
            default: true,
        },
        {
            id: "showStoreHighlights",
            type: "checkbox",
            label: {
                ar: "إظهار شارات الثقة (شحن، دفع، إرجاع)",
                en: "Show trust badges (shipping, payment, returns)",
            },
            default: true,
        },
        {
            id: "stickyAddToCart",
            type: "checkbox",
            // The sticky buy bar (`BuyerProductStickyBar`) is `lg:hidden` — phone +
            // tablet only; desktop keeps the inline CTA stack. This toggle and its
            // style below therefore only affect mobile + tablet.
            appliesTo: ["mobile", "tablet"],
            label: {
                ar: 'تثبيت زر "أضف للسلة" أسفل الشاشة على الجوال',
                en: "Sticky add-to-cart on mobile",
            },
            default: true,
        },
        {
            id: "stickyBarStyle",
            type: "select",
            appliesTo: ["mobile", "tablet"],
            label: { ar: "شكل الشريط السفلي", en: "Sticky bar style" },
            helpText: {
                ar: 'شكل شريط "أضف للسلة" المثبت أسفل الشاشة على الجوال.',
                en: "How the mobile sticky add-to-cart bar looks.",
            },
            options: [
                {
                    value: "bar-qty",
                    label: { ar: "كمية + إضافة للسلة", en: "Quantity + Add to cart" },
                },
                {
                    value: "bar-price",
                    label: { ar: "السعر + إضافة للسلة", en: "Price + Add to cart" },
                },
            ],
            default: "bar-qty",
            // A9: the sticky bar style only matters when the sticky bar is enabled.
            showIf: { stickyAddToCart: true },
        },
        {
            id: "stickyBuyBarDesktop",
            type: "checkbox",
            // The desktop sticky buy bar (`BuyerProductDesktopBuyBar`) is `lg:block` —
            // desktop only; phones/tablets use the `stickyAddToCart` bar above. This
            // mirrors EasyOrders' platform-rendered desktop buy bar (the PDP parity gap).
            appliesTo: ["desktop"],
            label: {
                ar: "شريط شراء ثابت أسفل الشاشة على الكمبيوتر",
                en: "Sticky buy bar on desktop",
            },
            helpText: {
                ar: "شريط سفلي بصورة المنتج والسعر والكمية وزر الشراء على شاشات الكمبيوتر.",
                en: "A bottom bar with the product thumb, price, quantity and Buy-now on desktop.",
            },
            default: false,
        },
        {
            id: "stickyBuyBarBehavior",
            type: "select",
            appliesTo: ["desktop"],
            label: { ar: "ظهور الشريط", en: "Bar visibility" },
            helpText: {
                ar: "دايمًا ظاهر، أو يظهر بعد ما ينزل الزائر لتحت.",
                en: "Always visible, or revealed after the shopper scrolls down.",
            },
            options: [
                { value: "always-on", label: { ar: "دايمًا ظاهر", en: "Always visible" } },
                { value: "on-scroll", label: { ar: "يظهر عند النزول", en: "On scroll" } },
            ],
            default: "on-scroll",
            showIf: { stickyBuyBarDesktop: true },
        },
        {
            id: "stickyBuyBarStyle",
            type: "select",
            appliesTo: ["desktop"],
            label: { ar: "شكل الشريط", en: "Bar style" },
            helpText: {
                ar: "شريط بعرض الشاشة، أو بطاقة عائمة في الركن.",
                en: "A full-width bar, or a floating card anchored in the corner.",
            },
            options: [
                {
                    value: "full-width-bar",
                    label: { ar: "شريط كامل العرض", en: "Full-width bar" },
                },
                {
                    value: "floating-card",
                    label: { ar: "بطاقة عائمة", en: "Floating card" },
                },
            ],
            default: "full-width-bar",
            showIf: { stickyBuyBarDesktop: true },
        },
        // --- Per-family PDP typography (EO parity). CSS values applied as vars on
        //     the PDP root; empty ⇒ the component's built-in default (legacy stores
        //     unchanged). Populated per theme from the CSS audit. ----------------
        {
            id: "pdpTitleSize",
            type: "text",
            label: { ar: "حجم عنوان المنتج", en: "Product title size" },
            helpText: { ar: "مثال: 22px", en: "CSS size, e.g. 22px. Empty = default." },
            default: "",
        },
        {
            id: "pdpTitleWeight",
            type: "text",
            label: { ar: "سماكة العنوان", en: "Product title weight" },
            helpText: { ar: "مثال: 400", en: "CSS weight, e.g. 400. Empty = default." },
            default: "",
        },
        {
            id: "pdpTitleTransform",
            type: "select",
            label: { ar: "حالة أحرف العنوان", en: "Product title case" },
            options: [
                { value: "none", label: { ar: "عادي", en: "Normal" } },
                { value: "uppercase", label: { ar: "أحرف كبيرة", en: "UPPERCASE" } },
            ],
            default: "none",
        },
        {
            id: "pdpTitleTracking",
            type: "text",
            label: { ar: "تباعد أحرف العنوان", en: "Product title tracking" },
            helpText: { ar: "مثال: 0.18em", en: "CSS letter-spacing. Empty = default." },
            default: "",
        },
        {
            id: "pdpButtonRadius",
            type: "text",
            label: { ar: "تدوير أزرار المنتج", en: "Product button radius" },
            helpText: { ar: "مثال: 0px", en: "CSS radius, e.g. 0px. Empty = theme default." },
            default: "",
        },
        // --- Per-family PDP price typography (EO parity). Empty ⇒ the component's
        //     built-in default (22px/600/--fg, formerly monospace). Populated per
        //     family from the CSS audit; drops the legacy mono face on the price. --
        {
            id: "pdpPriceSize",
            type: "text",
            label: { ar: "حجم السعر", en: "Price size" },
            helpText: { ar: "مثال: 14px", en: "CSS size, e.g. 14px. Empty = default." },
            default: "",
        },
        {
            id: "pdpPriceWeight",
            type: "text",
            label: { ar: "سماكة السعر", en: "Price weight" },
            helpText: { ar: "مثال: 700", en: "CSS weight, e.g. 700. Empty = default." },
            default: "",
        },
        {
            id: "pdpPriceColor",
            type: "text",
            label: { ar: "لون السعر", en: "Price color" },
            helpText: { ar: "مثال: #111827", en: "CSS color. Empty = theme --fg." },
            default: "",
        },
        // --- Per-family PDP gallery image surface (EO parity). EO tiles the product
        //     image on a soft-grey wrapper; empty ⇒ white (legacy behaviour). -------
        {
            id: "pdpImageBg",
            type: "text",
            label: { ar: "خلفية صور المنتج", en: "Gallery image surface" },
            helpText: { ar: "مثال: #EEEEEE", en: "CSS color behind gallery images. Empty = white." },
            default: "",
        },
        // --- Per-family PDP buy-button typography (EO parity). Empty ⇒ component
        //     defaults (15px/600, no transform, 0 20px padding, accent border). ------
        {
            id: "pdpButtonSize",
            type: "text",
            label: { ar: "حجم خط الأزرار", en: "Button font size" },
            helpText: { ar: "مثال: 13px", en: "CSS size, e.g. 13px. Empty = default." },
            default: "",
        },
        {
            id: "pdpButtonWeight",
            type: "text",
            label: { ar: "سماكة خط الأزرار", en: "Button font weight" },
            helpText: { ar: "مثال: 500", en: "CSS weight, e.g. 500. Empty = default." },
            default: "",
        },
        {
            id: "pdpButtonTransform",
            type: "select",
            label: { ar: "حالة أحرف الأزرار", en: "Button text case" },
            options: [
                { value: "none", label: { ar: "عادي", en: "Normal" } },
                { value: "uppercase", label: { ar: "أحرف كبيرة", en: "UPPERCASE" } },
                { value: "capitalize", label: { ar: "أول حرف كبير", en: "Capitalize" } },
            ],
            default: "none",
        },
        {
            id: "pdpButtonPadding",
            type: "text",
            label: { ar: "حشو الأزرار", en: "Button padding" },
            helpText: { ar: "مثال: 10.4px 28px", en: "CSS padding. Empty = default." },
            default: "",
        },
        {
            id: "pdpButtonBorderColor",
            type: "text",
            label: { ar: "لون إطار زر الإضافة", en: "Add-to-cart border color" },
            helpText: { ar: "مثال: #D8D8D8", en: "CSS color for the outline Add-to-cart border. Empty = accent." },
            default: "",
        },
        {
            id: "showPolicies",
            type: "checkbox",
            label: { ar: "إظهار روابط سياسات المتجر", en: "Show store policies" },
            default: true,
        },
    ],
    presets: [
        {
            name: { ar: "أعمدة", en: "Columns" },
            description: {
                ar: "شريط صور مصغّرة جنب الصورة الكبيرة وعمود معلومات ثابت — شكل الـ PDP الحالي.",
                en: "Thumb-rail slider beside a sticky info column — the current PDP.",
            },
            settings: {
                variant: "columns",
                thumbPosition: "left",
                infoColumnWidth: "medium",
                descriptionPlacement: "accordion-below",
            },
        },
        {
            name: { ar: "شرائح متحركة", en: "Carousel" },
            description: {
                ar: "معرض شرائح بأسهم بدون شريط صور مصغّرة.",
                en: "Swipe/arrow track carousel with no thumbnail rail.",
            },
            settings: {
                variant: "carousel",
                carouselControls: "arrows",
                infoColumnWidth: "medium",
            },
        },
        {
            name: { ar: "صور متتابعة", en: "Stacked" },
            description: {
                ar: "كل الصور فوق بعض في العمود، والوصف جوه التفاصيل.",
                en: "All images stacked in the column, description inside the details.",
            },
            settings: {
                variant: "stacked",
                infoColumnWidth: "wide",
                descriptionPlacement: "in-details",
            },
        },
        {
            name: { ar: "فسيفساء", en: "Mosaic" },
            description: {
                ar: "شبكة صور من ٣ أعمدة في العمود الأيسر.",
                en: "A 3-column image grid in the left column.",
            },
            settings: {
                variant: "mosaic",
                mosaicColumns: "3",
                infoColumnWidth: "medium",
            },
        },
    ],
};
//# sourceMappingURL=product-details.js.map